import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ThemeToggle } from '@/components/ThemeToggle';
import {
  FileText, ArrowLeft, Copy, Check, Gift, Wallet, Users, IndianRupee,
  Share2, MessageCircle, Mail, Linkedin, Twitter, Sparkles, Rocket,
  UserPlus, CheckCircle2, ChevronRight,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HOW_IT_WORKS = [
  { icon: Share2, title: 'Share your link', desc: 'Send your unique referral link to friends, classmates, or your network.' },
  { icon: UserPlus, title: 'They sign up & subscribe', desc: 'Your friend gets a 10% discount on their first Aarova AI Pro or Plus plan.' },
  { icon: Wallet, title: 'You earn 10% in wallet', desc: 'We instantly credit 10% of their plan value to your Aarova wallet — redeem it on your next subscription.' },
];

const FAQS = [
  { q: 'When do I get my 10% reward?', a: 'The reward is credited to your wallet the moment your referred friend successfully purchases any paid subscription (Pro or Plus).' },
  { q: 'How much can I earn?', a: 'There is no cap. Earn ₹10 on every ₹99 Pro plan and ₹30 on every ₹299 Plus plan your friend buys — across monthly, quarterly, and yearly cycles.' },
  { q: 'How do I use my wallet balance?', a: 'Your wallet auto-applies on checkout. When you upgrade or renew, you can choose to redeem wallet credits to reduce your bill — up to the full amount.' },
  { q: 'Does my friend get anything?', a: 'Yes — every friend who joins through your link gets a 10% discount on their first paid plan.' },
  { q: 'Is there a limit on referrals?', a: 'No limits. Refer as many people as you want and keep stacking your wallet.' },
];

function StatCard({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${accent || 'text-orange-500'}`} />
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500">{label}</p>
      </div>
      <p className={`text-3xl font-black ${accent || 'text-slate-900 dark:text-white'}`} style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{value}</p>
      {sub && <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">{sub}</p>}
    </div>
  );
}

function ShareButton({ icon: Icon, label, href, color, testId }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-bold text-white transition-transform hover:-translate-y-0.5 active:scale-95 ${color}`}
    >
      <Icon className="w-4 h-4" /> {label}
    </a>
  );
}

export default function ReferAndEarnPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/referral/my-stats`, { withCredentials: true });
      setData(data);
    } catch (_) {
      toast.error('Could not load referral data. Please log in.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const copy = async () => {
    if (!data?.referral_url) return;
    try {
      await navigator.clipboard.writeText(data.referral_url);
      setCopied(true);
      toast.success('Referral link copied');
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error('Copy failed');
    }
  };

  const shareMsg = data ? `I'm using Aarova AI to build ATS-friendly resumes and find AI-matched jobs. Sign up with my link and get 10% off your first subscription: ${data.referral_url}` : '';
  const shareEncoded = encodeURIComponent(shareMsg);

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-3.5 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 group" data-testid="brand-link">
            <FileText className="w-7 h-7 text-orange-500 group-hover:scale-110 transition-transform" strokeWidth={2} />
            <span className="text-xl font-black text-slate-900 dark:text-white tracking-tight" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Aarova AI</span>
            <span className="hidden sm:inline-flex items-center gap-1 ml-2 text-[10px] font-bold uppercase tracking-widest text-orange-600 dark:text-orange-400 bg-orange-100/60 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/20 px-2 py-0.5 rounded-full">
              <Gift className="w-2.5 h-2.5" /> Refer &amp; Earn
            </span>
          </button>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button data-testid="refer-back-btn" onClick={() => navigate('/dashboard')} variant="ghost" className="text-xs font-semibold text-slate-700 dark:text-slate-300 hover:text-orange-600">
              <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-12 py-10 md:py-14 space-y-10">
        {/* Hero */}
        <section className="relative overflow-hidden text-center">
          <div className="absolute inset-0 -z-10">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-orange-500/10 dark:bg-orange-500/20 blur-3xl rounded-full" />
          </div>
          <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-orange-600 dark:text-orange-300 bg-orange-100/80 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 px-3 py-1.5 rounded-full">
            <Sparkles className="w-3.5 h-3.5" /> Earn While You Share
          </span>
          <h1
            className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] text-slate-900 dark:text-white"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="refer-heading"
          >
            Refer Friends. <span className="bg-gradient-to-r from-orange-500 via-amber-400 to-orange-500 bg-clip-text text-transparent">Earn 10%</span> in Your Wallet.
          </h1>
          <p className="mt-5 max-w-xl mx-auto text-base sm:text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
            Share Aarova AI with friends — they get <b className="text-slate-900 dark:text-white">10% off</b> their first subscription, and you get <b className="text-orange-600 dark:text-orange-400">10% of their plan</b> credited instantly to your wallet.
          </p>
        </section>

        {/* Stats */}
        <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard icon={Wallet} label="Wallet Balance" value={`₹${data?.wallet_balance ?? 0}`} sub="Redeemable on next plan" accent="text-emerald-600 dark:text-emerald-400" />
          <StatCard icon={IndianRupee} label="Total Earned" value={`₹${data?.total_earned ?? 0}`} sub="All-time rewards" accent="text-orange-600 dark:text-orange-400" />
          <StatCard icon={Users} label="Friends Joined" value={data?.total_referrals ?? 0} sub="Signed up via your link" />
          <StatCard icon={CheckCircle2} label="Paid Conversions" value={data?.paid_referrals ?? 0} sub="Subscribed friends" accent="text-sky-600 dark:text-sky-400" />
        </section>

        {/* Referral Link + Code */}
        <section className="relative">
          <div className="absolute -inset-px bg-gradient-to-r from-orange-500/30 via-amber-400/20 to-orange-500/30 rounded-2xl blur-md opacity-60 dark:opacity-100" />
          <div className="relative bg-white dark:bg-slate-900/90 backdrop-blur-xl border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8">
            <div className="flex items-center gap-2 mb-4">
              <div className="w-8 h-8 rounded-lg bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center">
                <Share2 className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
              <h2 className="text-lg font-black text-slate-900 dark:text-white" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Your Referral Link</h2>
            </div>

            {loading ? (
              <div className="h-12 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
            ) : (
              <>
                <div className="flex flex-col sm:flex-row gap-2 mb-4">
                  <code data-testid="referral-link" className="flex-1 px-4 py-3 bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg text-sm font-mono text-slate-700 dark:text-slate-300 truncate">
                    {data?.referral_url}
                  </code>
                  <Button
                    data-testid="copy-referral-btn"
                    onClick={copy}
                    className="h-12 px-5 bg-slate-900 dark:bg-white hover:bg-orange-500 dark:hover:bg-orange-500 dark:hover:text-white text-white dark:text-slate-900 font-bold rounded-lg transition-colors"
                  >
                    {copied ? <><Check className="w-4 h-4 mr-1.5" /> Copied</> : <><Copy className="w-4 h-4 mr-1.5" /> Copy Link</>}
                  </Button>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="text-slate-500 dark:text-slate-400">Your code:</span>
                  <code data-testid="referral-code" className="font-mono font-bold text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-500/10 border border-orange-200 dark:border-orange-500/30 px-2 py-0.5 rounded">
                    {data?.referral_code}
                  </code>
                </div>

                <div className="mt-5 pt-5 border-t border-slate-100 dark:border-slate-800">
                  <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-500 dark:text-slate-500 mb-3">Share on</p>
                  <div className="flex flex-wrap gap-2">
                    <ShareButton icon={MessageCircle} label="WhatsApp" href={`https://wa.me/?text=${shareEncoded}`} color="bg-emerald-500 hover:bg-emerald-600" testId="share-whatsapp" />
                    <ShareButton icon={Linkedin} label="LinkedIn" href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(data?.referral_url || '')}`} color="bg-sky-600 hover:bg-sky-700" testId="share-linkedin" />
                    <ShareButton icon={Twitter} label="Twitter / X" href={`https://twitter.com/intent/tweet?text=${shareEncoded}`} color="bg-slate-900 hover:bg-slate-800" testId="share-twitter" />
                    <ShareButton icon={Mail} label="Email" href={`mailto:?subject=Try%20Aarova%20AI&body=${shareEncoded}`} color="bg-orange-500 hover:bg-orange-600" testId="share-email" />
                  </div>
                </div>
              </>
            )}
          </div>
        </section>

        {/* How it works */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>How it works</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {HOW_IT_WORKS.map((step, i) => (
              <div key={step.title} data-testid={`how-step-${i + 1}`} className="relative bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-6">
                <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-gradient-to-br from-orange-500 to-amber-500 text-white text-sm font-black flex items-center justify-center shadow-lg shadow-orange-500/30" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {i + 1}
                </div>
                <div className="w-10 h-10 rounded-xl bg-orange-100 dark:bg-orange-500/15 flex items-center justify-center mb-3">
                  <step.icon className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                </div>
                <h3 className="font-bold text-slate-900 dark:text-white mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{step.title}</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Earnings table */}
        <section className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8">
          <h2 className="text-lg font-black text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>How much you earn per friend</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 dark:text-slate-500 border-b border-slate-200 dark:border-slate-800">
                  <th className="py-2 font-bold">Plan</th>
                  <th className="py-2 font-bold">Plan Price</th>
                  <th className="py-2 font-bold">Friend Pays</th>
                  <th className="py-2 font-bold text-right">You Earn</th>
                </tr>
              </thead>
              <tbody className="text-slate-700 dark:text-slate-300">
                {[
                  { plan: 'Pro · Monthly', price: 99, name: 'Pro' },
                  { plan: 'Pro · Yearly', price: 831, name: 'Pro' },
                  { plan: 'Plus · Monthly', price: 299, name: 'Plus' },
                  { plan: 'Plus · Yearly', price: 2511, name: 'Plus' },
                ].map((p) => {
                  const friend = Math.round(p.price * 0.9);
                  const you = Math.round(p.price * 0.1);
                  return (
                    <tr key={p.plan} className="border-b border-slate-100 dark:border-slate-800/60">
                      <td className="py-3 font-semibold">{p.plan}</td>
                      <td className="py-3">₹{p.price}</td>
                      <td className="py-3">₹{friend} <span className="text-[10px] text-slate-400">(10% off)</span></td>
                      <td className="py-3 text-right font-black text-orange-600 dark:text-orange-400">₹{you}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* Recent referrals */}
        {data?.referrals?.length > 0 && (
          <section className="bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 md:p-8">
            <h2 className="text-lg font-black text-slate-900 dark:text-white mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Recent referrals</h2>
            <div className="divide-y divide-slate-100 dark:divide-slate-800">
              {data.referrals.map((r, i) => (
                <div key={i} className="flex items-center justify-between py-3" data-testid={`referral-row-${i}`}>
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 flex items-center justify-center text-xs font-black text-white">
                      {(r.name || 'U').slice(0, 1).toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-slate-900 dark:text-white truncate">{r.name}</p>
                      <p className="text-xs text-slate-500 dark:text-slate-500 truncate">{r.email_masked}</p>
                    </div>
                  </div>
                  {r.is_paid ? (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">
                      Paid ✓
                    </span>
                  ) : (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                      Signed up
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FAQs */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight mb-6" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>FAQs</h2>
          <div className="space-y-2">
            {FAQS.map((f, i) => (
              <details key={i} data-testid={`faq-${i}`} className="group bg-white dark:bg-slate-900/80 border border-slate-200 dark:border-slate-800 rounded-xl p-4 open:shadow-sm">
                <summary className="flex items-center justify-between cursor-pointer text-sm font-bold text-slate-900 dark:text-white">
                  {f.q}
                  <ChevronRight className="w-4 h-4 text-slate-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="mt-3 text-sm text-slate-600 dark:text-slate-400 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* CTA strip */}
        <section className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 text-white p-8 md:p-10 text-center">
          <div className="absolute -top-10 -right-10 w-48 h-48 bg-orange-500/30 rounded-full blur-3xl" />
          <div className="relative">
            <h3 className="text-2xl sm:text-3xl font-black mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>Start earning today</h3>
            <p className="text-slate-400 mb-5">Copy your link, share with one friend, and watch your wallet grow.</p>
            <Button
              data-testid="cta-copy-btn"
              onClick={copy}
              className="h-12 px-6 bg-orange-500 hover:bg-orange-600 text-slate-950 font-bold rounded-lg shadow-lg shadow-orange-500/30"
            >
              <Rocket className="w-4 h-4 mr-2" /> Copy My Referral Link
            </Button>
          </div>
        </section>
      </main>
    </div>
  );
}
